import React from 'react';
import { List, Datagrid, TextField, DateField, BooleanField, Create, Edit, SimpleForm, TextInput, SelectInput, BooleanInput, required, useNotify, useRedirect, SaveButton, Toolbar, TabbedForm, FormTab, useRecordContext, Button, useUpdate, useRefresh, useInput, TopToolbar, useDataProvider, FunctionField, ChipField } from 'react-admin';
import { Box, Typography, TextField as MuiTextField, Grid, IconButton, Chip, CircularProgress, Checkbox, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, Accordion, AccordionSummary, AccordionDetails, Button as MuiButton } from '@mui/material';
import { Refresh as RefreshIcon, Download as DownloadIcon, ExpandMore as ExpandMoreIcon, Info as InfoIcon } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { Alert, Card, CardContent, CardHeader, FormControl, FormLabel, RadioGroup, Radio, Divider, Snackbar } from '@mui/material';
import { FileInput, FileField, FormDataConsumer } from 'react-admin';
import { TabbedAnalyticsDashboard } from '../mm/components/TabbedAnalyticsDashboard';
import { ConfigValidationPanel } from '../mm/components/ConfigValidationPanel';
import { useConfigValidation } from '../mm/hooks/useConfigValidation';
import { DashboardAiProvider } from '../mm/contexts/DashboardAiContext';
import { getTemplate, getTemplateOptions } from './dashboard-templates.registry';
import { forceRefreshTemplates } from './template-loader';
import JsonEditorAce from '../../../components/code/ace/json.editor';
import TemplateEditorAce from '../../../components/code/ace/template.editor';
import AIFunctionEdit from '../../ai/assistant/AIFunctionEdit';
import ChatInterface from '../../ai/chat/ChatInterface';
import { 
  AgenticFeatureRegistry, 
  initializeAnalyticsDashboardProvider 
} from '../../ai/chat';
import { useAgenticContext } from '../../ai/chat/hooks/useAgenticContext';

// Make forceRefreshTemplates globally accessible for debugging
(window as any).forceRefreshTemplates = forceRefreshTemplates;

// Context for sharing derived application across tabs and actions
// Defined at module level so it can be used by all components
const DerivedApplicationContext = React.createContext<{
  derivedApplication: any;
  loading: boolean;
  error: string | null;
  refetch: () => void;
} | null>(null);

// Custom component for formatted date display in forms
const FormattedDateField: React.FC<{ source: string; label: string; helperText?: string }> = ({ source, label, helperText }) => {
  const record = useRecordContext();
  const value = source.split('.').reduce((obj, key) => obj?.[key], record);
  const formattedDate = value ? new Date(value).toLocaleString() : 'N/A';
  
  return (
    <MuiTextField 
      label={label}
      value={formattedDate}
      disabled
      fullWidth
      margin="normal"
      helperText={helperText}
      variant="outlined"
    />
  );
};

export const AnalyticsDashboardList = () => {
  // Initialize the analytics dashboard provider (registers event listeners)
  useEffect(() => {
    initializeAnalyticsDashboardProvider();
  }, []);
  
  // Note: Feature context is now managed by route-based context in App.tsx

  return (
    <List>
      <Datagrid rowClick="edit">
        <TextField source="name" />
        <TextField source="key" />
        <TextField source="description" />
        <FunctionField
          label="Version"
          render={(record: any) => {
            const version = record.currentVersion || 1;
            const label = record.versionLabel;
            return label ? `v${version} (${label})` : `v${version}`;
          }}
        />
        <FunctionField
          label="Status"
          render={(record: any) => (
            <Chip
              label={record.published ? 'Published' : 'Draft'}
              color={record.published ? 'success' : 'default'}
              size="small"
              sx={{
                fontWeight: 500,
                backgroundColor: record.published ? '#4caf50' : '#9e9e9e',
                color: 'white'
              }}
            />
          )}
        />
        <TextField source="templateId" label="Template" />
      </Datagrid>
    </List>
  );
};  

const DashboardToolbar: React.FC<any> = props => (
  <Toolbar {...props}>
    <SaveButton />
  </Toolbar>
);

const handleExportDashboard = (record: any) => {
  if (!record) return;

  // If exportMetadata already exists (from enhanced export), preserve it
  // Otherwise create default metadata for backward compatibility
  const exportData = {
    name: record.name,
    key: record.key,
    description: record.description,
    templateId: record.templateId,
    template: record.template,
    currentConfig: record.currentConfig,
    // NEW: Include function implementations if present
    ...(record.functionImplementations && record.functionImplementations.length > 0 && {
      functionImplementations: record.functionImplementations
    }),
    // Use provided exportMetadata or create default
    exportMetadata: record.exportMetadata || {
      exportedAt: new Date().toISOString(),
      exportedBy: "Admin Portal",
      version: "1.0",
      type: "analytics-dashboard"
    }
  };

  // Create and download the file
  const dataStr = JSON.stringify(exportData, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${record.name || 'analytics-dashboard'}-export.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Wrapper component that provides derived application context
const EditDashboardActionsWrapper = () => {
  const record = useRecordContext();
  const derivedAppData = useDerivedApplication(record);
  
  return (
    <DerivedApplicationContext.Provider value={derivedAppData}>
      <EditDashboardActions />
    </DerivedApplicationContext.Provider>
  );
};

const EditDashboardActions = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const derivedAppContext = React.useContext(DerivedApplicationContext);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    includeImplementations: true,
    includeOpenApiSpec: false,
    includeTemplate: true,
    includeVersionHistory: false,
    includeMetadata: true
  });

  const handleExportWithOpenApiSpec = async () => {
    setExportDialogOpen(true);
  };

  const handleConfirmExport = async () => {
    setExportDialogOpen(false);
    try {
      // Fetch dashboard with optional OpenAPI spec
      const fieldsToSelect = exportOptions.includeOpenApiSpec ? ['openApiSpec'] : [];
      const fullRecord = await (dataProvider as any).getOneWithSelect('analytics-dashboards', record.id, fieldsToSelect);
      
      console.log('📤 [Export] Starting export for dashboard:', fullRecord.key);
      console.log('📤 [Export] Options:', exportOptions);
      console.log('📤 [Export] Derived app context available:', !!derivedAppContext);
      console.log('📤 [Export] Derived app in context:', derivedAppContext?.derivedApplication?.key || 'none');
      
      // Use derived application from shared context
      let functionImplementations = [];
      const derivedApp = derivedAppContext?.derivedApplication;
      
      if (exportOptions.includeImplementations && derivedApp) {
        console.log('✅ [Export] Found derived application:', derivedApp.key);
        console.log('✅ [Export] Application configuration:', derivedApp.configuration ? 'present' : 'missing');
        console.log('✅ [Export] Functions array:', derivedApp.configuration?.functions?.length || 0);
        
        // Extract function implementations (portable across environments)
        if (derivedApp.configuration?.functions && Array.isArray(derivedApp.configuration.functions)) {
          // Log each function to see what we have
          derivedApp.configuration.functions.forEach((fn: any, idx: number) => {
            console.log(`  Function ${idx}: ${fn.name} (${fn.operationKey})`);
            console.log(`    - executionProviderKey: ${fn.executionProviderKey}`);
            console.log(`    - implementationConfig: ${fn.implementationConfig ? 'present' : 'missing'}`);
            console.log(`    - mock: ${fn.mock ? 'present' : 'missing'}`);
            console.log(`    - webhookUrl: ${fn.webhookUrl ? 'present' : 'missing'}`);
          });
          
          functionImplementations = derivedApp.configuration.functions.map((fn: any) => ({
            operationKey: fn.operationKey,
            name: fn.name,
            description: fn.description,
            method: fn.method,
            path: fn.path,
            executionProviderKey: fn.executionProviderKey,
            implementationConfig: fn.implementationConfig,
            requiresAuthentication: fn.requiresAuthentication,
            // Backward compatibility with legacy fields
            mock: fn.mock,
            webhookUrl: fn.webhookUrl
          }));
          console.log(`📦 [Export] Extracted ${functionImplementations.length} function implementations from context`);
        } else {
          console.log('⚠️ [Export] Derived app has no functions array or empty array');
        }
      } else if (!exportOptions.includeImplementations) {
        console.log('ℹ️ [Export] Function implementations excluded by user option');
      } else {
        console.log('⚠️ [Export] No derived application in context - exporting dashboard only');
        console.log('⚠️ [Export] Context state - loading:', derivedAppContext?.loading, 'error:', derivedAppContext?.error);
      }
      
      // Create clean export data with ONLY selected fields
      const exportData: any = {
        name: fullRecord.name,
        key: fullRecord.key,
        description: fullRecord.description,
        published: fullRecord.published,
        currentConfig: fullRecord.currentConfig
      };

      // Optionally include template
      if (exportOptions.includeTemplate) {
        exportData.templateId = fullRecord.templateId;
        exportData.template = fullRecord.template;
      }

      // Optionally include OpenAPI spec
      if (exportOptions.includeOpenApiSpec && fullRecord.openApiSpec) {
        exportData.openApiSpec = fullRecord.openApiSpec;
      }

      // Optionally include version history
      if (exportOptions.includeVersionHistory && fullRecord.versions?.length > 0) {
        exportData.versions = fullRecord.versions;
      }

      // Optionally include function implementations
      if (exportOptions.includeImplementations && functionImplementations.length > 0) {
        exportData.functionImplementations = functionImplementations;
      }

      // Add export metadata
      if (exportOptions.includeMetadata) {
        exportData.exportMetadata = {
          exportedAt: new Date().toISOString(),
          exportedBy: "Admin Portal",
          version: (exportOptions.includeImplementations && functionImplementations.length > 0) ? "2.0" : "1.0",
          type: "analytics-dashboard",
          includesImplementations: exportOptions.includeImplementations && functionImplementations.length > 0,
          implementationCount: exportOptions.includeImplementations ? functionImplementations.length : 0,
          exportOptions: { ...exportOptions }
        };
      }
      
      handleExportDashboard(exportData);
      
      // Build success message
      const includedParts = [];
      if (exportOptions.includeTemplate) includedParts.push('template');
      if (exportOptions.includeImplementations && functionImplementations.length > 0) {
        includedParts.push(`${functionImplementations.length} implementation(s)`);
      }
      if (exportOptions.includeOpenApiSpec) includedParts.push('OpenAPI spec');
      if (exportOptions.includeVersionHistory) includedParts.push('version history');
      
      const message = includedParts.length > 0 
        ? `Dashboard exported with: ${includedParts.join(', ')}`
        : 'Dashboard exported (configuration only)';
      
      notify(message, { type: 'success' });
    } catch (error) {
      console.error('Error fetching full dashboard for export:', error);
      notify('Failed to export dashboard', { type: 'error' });
    }
  };

  return (
    <>
      <TopToolbar>
        <IconButton
          onClick={handleExportWithOpenApiSpec}
          title="Export Dashboard"
          color="primary"
        >
          <DownloadIcon />
        </IconButton>
      </TopToolbar>

      {/* Export Options Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Dashboard Options</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose what to include in your dashboard export. The dashboard configuration will always be included.
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportOptions.includeTemplate}
                  onChange={(e) => setExportOptions({ ...exportOptions, includeTemplate: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2"><strong>Template Configuration</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include the original template used to create this dashboard (for refresh functionality)
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportOptions.includeImplementations}
                  onChange={(e) => setExportOptions({ ...exportOptions, includeImplementations: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2"><strong>Function Implementations</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    {derivedAppContext?.derivedApplication?.configuration?.functions?.length 
                      ? `Include ${derivedAppContext.derivedApplication.configuration.functions.length} function implementation(s) (SQL queries, mock data, webhooks)`
                      : 'No implementations available to export'
                    }
                  </Typography>
                </Box>
              }
              disabled={!derivedAppContext?.derivedApplication?.configuration?.functions?.length}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportOptions.includeOpenApiSpec}
                  onChange={(e) => setExportOptions({ ...exportOptions, includeOpenApiSpec: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2"><strong>OpenAPI Specification</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include the generated OpenAPI spec (usually not needed - will be regenerated on import)
                  </Typography>
                </Box>
              }
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportOptions.includeVersionHistory}
                  onChange={(e) => setExportOptions({ ...exportOptions, includeVersionHistory: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2"><strong>Version History</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include historical configuration versions (if any exist)
                  </Typography>
                </Box>
              }
              disabled={!record?.versions?.length}
            />
            
            <Divider sx={{ my: 2 }} />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={exportOptions.includeMetadata}
                  onChange={(e) => setExportOptions({ ...exportOptions, includeMetadata: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2"><strong>Export Metadata</strong></Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include export timestamp, version info, and import hints (recommended)
                  </Typography>
                </Box>
              }
            />
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>Note:</strong> Database IDs, timestamps, and runtime data are automatically excluded from exports.
              Only configuration and selected implementation data will be included.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)} label="Cancel" />
          <Button 
            onClick={handleConfirmExport} 
            label="Export" 
            variant="contained"
            color="primary"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

const EditDashboardToolbar: React.FC<any> = props => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update] = useUpdate();


  const handleRefreshFromTemplate = async () => {
    // Try to determine template from record or use a default

    let templateId = record?.templateId;
    
    try {
      // Properly await the async template loading
      const selectedTemplate = await getTemplate(templateId);
      
      if (!selectedTemplate || !selectedTemplate.config) {
        notify(`Template '${templateId}' not found or invalid. Please check available templates`, { type: 'error' });
        return;
      }

      // Update the dashboard with the latest template configuration
      await update(
        'analytics-dashboards',
        {
          id: record.id,
          data: {
            ...record,
            currentConfig: {
              config: selectedTemplate.config
            }
          },
          previousData: record
        },
        {
          onSuccess: () => {
            notify('Dashboard refreshed from template successfully!', { type: 'success' });
            refresh();
          },
          onError: (error: any) => {
            notify(`Failed to refresh dashboard: ${error.message}`, { type: 'error' });
          }
        }
      );
    } catch (error: any) {
      notify(`Error refreshing dashboard: ${error.message}`, { type: 'error' });
    }
  };

  return (
    <Toolbar {...props}>
      <SaveButton />
      <Button
        label={record?.id ? "Refresh From Template" : "No Template (Refresh Disabled)"}
        onClick={handleRefreshFromTemplate}
        disabled={!record?.id}
        startIcon={<RefreshIcon />}
        variant="outlined"
        sx={{ ml: 1 }}
        title={record?.id ? `Refresh from template: ${record.id}` : 'No template specified - button disabled'}
      />
    </Toolbar>
  );
};

interface DashboardCreateFormProps {
  creationMethod: string;
  setCreationMethod: (method: string) => void;
  selectedTemplateConfig: any;
  setSelectedTemplateConfig: (config: any) => void;
  importedData: any;
  setImportedData: (data: any) => void;
}

const DashboardCreateForm: React.FC<DashboardCreateFormProps> = ({
  creationMethod,
  setCreationMethod,
  selectedTemplateConfig,
  setSelectedTemplateConfig,
  importedData,
  setImportedData
}) => {
  const [fileProcessed, setFileProcessed] = useState(false);
  const notify = useNotify();
  
  const [isLoadingTemplate, setIsLoadingTemplate] = React.useState(false);
  const [templateOptions, setTemplateOptions] = React.useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingOptions, setIsLoadingOptions] = React.useState(true);

  // Reset when creation method changes
  useEffect(() => {
    setFileProcessed(false);
    setImportedData(null);
    setSelectedTemplateConfig(null);
  }, [creationMethod, setImportedData, setSelectedTemplateConfig]);

  // Handle file upload for import
  const handleFileUpload = (file: File, fieldName: string) => {
    if (fieldName === 'importedConfigurationFile' && file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importData = JSON.parse(e.target?.result as string);
          
          // Validate that this is a dashboard export
          if (!importData.currentConfig || importData.exportMetadata?.type !== 'analytics-dashboard') {
            notify('Invalid dashboard export file. Missing required fields or wrong type.', { type: 'error' });
            return;
          }

          setImportedData(importData);
          setFileProcessed(true);
          
          // Enhanced notification based on export version
          const version = importData.exportMetadata?.version || '1.0';
          const hasImplementations = importData.exportMetadata?.includesImplementations || false;
          const implCount = importData.exportMetadata?.implementationCount || 0;
          
          if (hasImplementations && implCount > 0) {
            notify(
              `Dashboard data imported successfully (v${version}). Includes ${implCount} function implementation(s) that will be applied after import.`, 
              { type: 'success' }
            );
          } else {
            notify(
              `Dashboard data imported successfully (v${version}). No function implementations included - functions will use default configurations.`, 
              { type: 'info' }
            );
          }
        } catch (error) {
          console.error('Error parsing import file:', error);
          notify('Error parsing import file. Please check the file format.', { type: 'error' });
        }
      };
      reader.readAsText(file);
    }
  };

  // Handle template selection change
  const handleTemplateChange = async (templateId: string) => {
    console.log('🔍 [Analytics Dashboard Create] Template change:', templateId);

    if (!templateId) {
      setSelectedTemplateConfig(null);
      return;
    }

    setIsLoadingTemplate(true);
    try {
      console.log('🔍 [Analytics Dashboard Create] Loading template:', templateId);
      const template = await getTemplate(templateId);
      console.log('🔍 [Analytics Dashboard Create] Template loaded:', template);

      if (template?.config) {
        console.log('🔍 [Analytics Dashboard Create] Template config loaded:', template.config);
        setSelectedTemplateConfig(template.config);
      } else {
        notify(`Failed to load template: ${templateId}`, { type: 'error' });
        setSelectedTemplateConfig(null);
      }
    } catch (error) {
      console.error('❌ Error loading template:', error);
      notify(`Error loading template: ${templateId}`, { type: 'error' });
      setSelectedTemplateConfig(null);
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  // Load template options on mount
  React.useEffect(() => {
    const loadTemplateOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const options = await getTemplateOptions();
        setTemplateOptions(options);
      } catch (error) {
        console.error('❌ Error loading template options:', error);
        notify('Failed to load template options', { type: 'error' });
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadTemplateOptions();
  }, []);

  return (
    <SimpleForm>
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardHeader title="Create Analytics Dashboard" />
          <CardContent>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Creation Method</FormLabel>
              <RadioGroup
                value={creationMethod}
                onChange={(e) => setCreationMethod(e.target.value)}
                row
              >
                <FormControlLabel 
                  value="template" 
                  control={<Radio />} 
                  label="From Template" 
                />
                <FormControlLabel 
                  value="import" 
                  control={<Radio />} 
                  label="Import Dashboard" 
                />
                <FormControlLabel 
                  value="manual" 
                  control={<Radio />} 
                  label="Manual Creation" 
                />
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>
      </Box>

      {/* Basic Dashboard Info */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Dashboard Details" />
        <CardContent>
          <TextInput 
            label="Name" 
            source="name" 
            fullWidth 
            sx={{ mb: 2 }} 
            disabled={creationMethod === 'import'}
            validate={creationMethod === 'import' ? () => undefined : required()}
          />
          <TextInput 
            label="Key" 
            source="key" 
            fullWidth 
            sx={{ mb: 2 }}
            disabled={creationMethod === 'import'}
            validate={creationMethod === 'import' ? () => undefined : required()}
          />
          <TextInput 
            label="Description" 
            source="description" 
            fullWidth 
            multiline
            disabled={creationMethod === 'import'}
          />
          {importedData && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Dashboard data imported successfully. Fields above are pre-filled from the import file.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Import includes: {importedData.currentConfig?.config?.tabs?.length || 0} tabs, 
                Template: {importedData.templateId || 'None'}
              </Typography>
              {importedData.exportMetadata?.includesImplementations && (
                <Box sx={{ mt: 1, p: 1, bgcolor: '#e8f5e9', borderRadius: 1, border: '1px solid #4caf50' }}>
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                    ✅ Function Implementations Included
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#2e7d32' }}>
                    {importedData.exportMetadata.implementationCount} function implementation(s) will be automatically applied after import
                  </Typography>
                </Box>
              )}
              {importedData.exportMetadata && !importedData.exportMetadata.includesImplementations && (
                <Box sx={{ mt: 1, p: 1, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                  <Typography variant="body2" sx={{ color: '#e65100' }}>
                    ℹ️ No Function Implementations
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#e65100' }}>
                    Dashboard structure will be imported, but functions will need to be configured manually
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Template Creation */}
      {creationMethod === 'template' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader 
            title="Create from Template" 
            subheader="Start with a pre-configured dashboard template"
          />
          <CardContent>
            <SelectInput 
              source="templateId" 
              choices={templateOptions}
              validate={required()}
              label="Dashboard Template"
              disabled={isLoadingOptions}
              helperText={
                isLoadingOptions
                  ? "Loading template options..."
                  : isLoadingTemplate 
                    ? "Loading template configuration..." 
                    : "Select a template to start with - this defines the initial dashboard configuration and structure"
              }
              onChange={(event: any) => {
                const value = event.target.value;
                if (value) {
                  handleTemplateChange(value);
                }
              }}
            />
            {selectedTemplateConfig && (
              <Box sx={{ 
                mt: 2, 
                p: 2, 
                backgroundColor: '#2e3440', 
                borderRadius: 1,
                border: '1px solid #4c566a'
              }}>
                <Typography variant="body2" sx={{ color: '#a3be8c' }}>
                  ✅ Template loaded: {selectedTemplateConfig.name || 'Unknown'}
                  <br />
                  <Typography component="span" sx={{ color: '#e5e9f0' }}>
                    {selectedTemplateConfig.description || 'No description available'}
                  </Typography>
                  <br />
                  <Typography component="span" sx={{ color: '#81a1c1' }}>
                    Tabs: {selectedTemplateConfig.tabs?.length || 0}
                  </Typography>
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dashboard Import */}
      {creationMethod === 'import' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader 
            title="Import Dashboard" 
            subheader="Import a complete dashboard from an exported JSON file"
          />
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Import a complete dashboard definition including all configurations, templates, and settings.
            </Alert>
            <FileInput 
              source="importedConfigurationFile" 
              label="Dashboard Export File" 
              accept=".json"
              onChange={(file) => {
                if (file && !fileProcessed) {
                  handleFileUpload(file, 'importedConfigurationFile');
                }
              }}
            >
              <FileField source="title" title="title" />
            </FileInput>
            {importedData && (
              <TextInput
                source="_enableSave"
                label="Enable Save"
                helperText="Type anything here to enable the save button (e.g., 'ready')"
                placeholder="Type 'ready' to enable save..."
                sx={{ mt: 2 }}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Manual Creation */}
      {creationMethod === 'manual' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader 
            title="Manual Creation" 
            subheader="Create an empty dashboard and configure manually"
          />
          <CardContent>
            <Alert severity="info">
              This will create an empty dashboard. You can add configurations and visualizations 
              after creation using the dashboard editor.
            </Alert>
          </CardContent>
        </Card>
      )}
    </SimpleForm>
  );
};

export const AnalyticsDashboardCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  
  // Lift state to parent so transform can access it
  const [creationMethod, setCreationMethod] = useState('template');
  const [selectedTemplateConfig, setSelectedTemplateConfig] = useState<any>(null);
  const [importedData, setImportedData] = useState(null);

  // Initialize the analytics dashboard provider (registers event listeners)
  useEffect(() => {
    initializeAnalyticsDashboardProvider();
  }, []);
  
  // Note: Feature context is now managed by route-based context in App.tsx

  const onSuccess = () => {
    notify('Analytics Dashboard created successfully');
    redirect('list', 'analytics-dashboards');
  };

  const transform = (data: any) => {
    console.log('🔍 [Analytics Dashboard Create] Transform called with data:', data);
    console.log('🔍 [Analytics Dashboard Create] Current state - creationMethod:', creationMethod);
    console.log('🔍 [Analytics Dashboard Create] Current state - selectedTemplateConfig:', selectedTemplateConfig);
    
    if (creationMethod === 'template') {
      if (!selectedTemplateConfig || !selectedTemplateConfig.id) {
        console.error('🔴 [Analytics Dashboard Create] Template configuration not loaded');
        throw new Error(`Template configuration not loaded. Please ensure a template is selected.`);
      }
      
      const transformed = {
        ...data,
        template: selectedTemplateConfig,
        currentConfig: {
          config: selectedTemplateConfig
        }
      };
      
      console.log('🔍 [Analytics Dashboard Create] Transformed data:', transformed);
      return transformed;
    } else if (creationMethod === 'import' && importedData) {
      // IMPORTANT: Remove the FileInput field and use our pre-processed importedData
      // Encode as base64 for backend processing
      const importJson = JSON.stringify(importedData);
      const base64Encoded = btoa(unescape(encodeURIComponent(importJson))); // Handle UTF-8
      
      console.log('🔍 [Analytics Dashboard Create] Encoding imported data');
      console.log('  - Original JSON length:', importJson.length);
      console.log('  - Base64 length:', base64Encoded.length);
      
      return {
        name: importedData.name,
        key: importedData.key,
        description: importedData.description,
        templateId: importedData.templateId,
        template: importedData.template,
        currentConfig: importedData.currentConfig,
        // Properly encoded for backend parseBase64EncodedFileContent
        importedConfiguration: base64Encoded,
        // Remove the FileInput field to prevent React-Admin from processing it
        importedConfigurationFile: undefined
      };
    } else if (creationMethod === 'manual') {
      // Manual creation: Initialize with empty configuration structure
      console.log('🔍 [Analytics Dashboard Create] Manual creation - initializing empty config');
      return {
        ...data,
        currentConfig: {
          config: {
            id: data.key,
            name: data.name,
            description: data.description,
            dataSource: 'api',
            tabs: []
          }
        }
      };
    }
    
    return data;
  };

  return (
    <Create mutationOptions={{ onSuccess }} transform={transform}>
      <DashboardCreateForm 
        creationMethod={creationMethod}
        setCreationMethod={setCreationMethod}
        selectedTemplateConfig={selectedTemplateConfig}
        setSelectedTemplateConfig={setSelectedTemplateConfig}
        importedData={importedData}
        setImportedData={setImportedData}
      />
    </Create>
  );
};

// Wrapper component that provides DashboardAiProvider with the derived application key
const DashboardAiProviderWrapper: React.FC<{ dashboardKey?: string; children: React.ReactNode }> = ({ dashboardKey, children }) => {
  const derivedAppContext = React.useContext(DerivedApplicationContext);
  const applicationKey = derivedAppContext?.derivedApplication?.key || undefined;
  
  // If we have a derived application, wrap with the AI provider
  // This enables AI insights to work in live mode
  if (applicationKey) {
    return (
      <DashboardAiProvider applicationKey={applicationKey}>
        {children}
      </DashboardAiProvider>
    );
  }
  
  // No derived application yet - just render children without AI provider
  // AI insight cards will show mock data in this case
  return <>{children}</>;
};

const PreviewTab: React.FC = () => {
  const record: any = useRecordContext();
  
  // Get the current form values to ensure we show the latest changes
  const configInput = useInput({ source: 'currentConfig.config' });
  const currentConfig = configInput.field.value || record?.currentConfig?.config;
  
  // Add key prop to force re-mounting when config changes significantly
  // This will clear cache and regenerate mock data - prioritizing timestamp-based approach
  const configKey = React.useMemo(() => {
    // Prioritize the persisted record's timestamp for cache busting
    // This ensures that a saved dashboard's preview is tied to its last update time
    if (record?.updatedAt) {
      const timestamp = new Date(record.updatedAt).getTime();
      const resultKey = `-config-${timestamp}`;
      return resultKey;
    }
    if (record?.createdAt) {
      const timestamp = new Date(record.createdAt).getTime();
      const resultKey = `-config-${timestamp}`;
      return resultKey;
    }

    // Fallback for unsaved changes or new dashboards: use a hash of the live config
    // This ensures that changes in the editor (especially mockDataConfig) still trigger updates
    if (!currentConfig) return 'no-config';

    const configForHashing = {
      dataSource: currentConfig.dataSource,
      mockDataConfig: currentConfig.mockDataConfig,
      dataSets: currentConfig.dataSets ? Object.keys(currentConfig.dataSets) : null,
      configId: currentConfig.id
    };

    const configString = JSON.stringify(configForHashing);
    let hash = 0;
    for (let i = 0; i < configString.length; i++) {
      const char = configString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    const resultKey = `hash-${Math.abs(hash)}`;
  
    return resultKey;
  }, [
    record?.updatedAt, // Depend on the persisted record's update time
    record?.createdAt, // Depend on the persisted record's creation time
    currentConfig?.mockDataConfig, // Depend on live mock data changes (for unsaved)
    currentConfig?.dataSource,     // Depend on live data source changes (for unsaved)
    currentConfig?.dataSets,       // Depend on live data sets changes (for unsaved)
    currentConfig?.id              // Depend on live config ID changes (for unsaved)
  ]);
  
  // Create a unique config for preview that ensures fresh mock data generation
  const previewConfig = React.useMemo(() => {
    if (!currentConfig) return currentConfig;
    
    // Update all dataSetId references in visualizations to point to the new cache-busted dataset
    const updateDataSetReferences = (obj: any): any => {
      if (!obj) return obj;
      
      // 🚀 FIX: Return primitive values unchanged to prevent string-to-object conversion
      if (typeof obj !== 'object') {
        return obj; // Return strings, numbers, booleans as-is
      }
      
      if (Array.isArray(obj)) {
        return obj.map(updateDataSetReferences);
      }
      
      const updated = { ...obj };
      
      // Update dataSetId references to match the DataSetManager virtual dataset naming
      // IMPORTANT: Include mode suffix (-preview) to prevent cache collisions with live mode
      if (updated.dataSet && updated.dataSet.dataSetId) {
        const originalDataSetId = updated.dataSet.dataSetId;
        
        // Use the actual dashboard config ID (clean, without corruption)
        let actualDashboardId = currentConfig.id;
        
        // Defensive: Clean any corrupted config IDs
        if (actualDashboardId.includes('-config-')) {
          actualDashboardId = actualDashboardId.split('-config-')[0];
          console.log(`🔧 [Dashboard Resource] Cleaned corrupted config ID: ${currentConfig.id} -> ${actualDashboardId}`);
        }
        
        // Generate dataset ID that matches SmartDataManager pattern:
        // ${dashboardId}${configKey}-preview-dataset (for preview mode)
        // This ensures preview mode (design mode) uses different cache keys than live mode
        const previewModeSuffix = '-preview';
        const newDataSetId = `${actualDashboardId}${configKey}${previewModeSuffix}-dataset`;
        
        updated.dataSet = {
          ...updated.dataSet,
          dataSetId: newDataSetId
        };
      }
      
      // Recursively update nested objects and arrays
      Object.keys(updated).forEach(key => {
        if (typeof updated[key] === 'object' && updated[key] !== null) {
          updated[key] = updateDataSetReferences(updated[key]);
        }
      });
      
      return updated;
    };
    
    return {
      ...currentConfig,
      // DON'T corrupt the config.id - keep it clean for proper API routing
      // Cache busting is now handled via stable dataset IDs in the services
      id: currentConfig.id,
      // Update tabs with corrected dataSet references
      tabs: updateDataSetReferences(currentConfig.tabs),
      // Keep mockDataConfig as-is - cache invalidation now handled through dataset IDs
      mockDataConfig: currentConfig.mockDataConfig,
      _previewMode: true,
      _cacheKey: configKey // Store cache key separately if needed for debugging
    };
  }, [currentConfig, configKey]);
  
  // Validate config - if it's a string, it means JSON parsing failed
  const isConfigValid = React.useMemo(() => {
    if (!currentConfig) return false;
    // If config is a string, it means the JSON editor has invalid JSON
    if (typeof currentConfig === 'string') return false;
    // Check if it has required properties
    if (!currentConfig.id || !currentConfig.tabs || !Array.isArray(currentConfig.tabs)) return false;
    return true;
  }, [currentConfig]);

  if (!currentConfig) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <p>No configuration available for preview</p>
        <p>The dashboard preview will appear here once a template is selected and saved.</p>
      </div>
    );
  }

  // Show error if config is invalid (e.g., invalid JSON string)
  if (!isConfigValid) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ⚠️ Invalid configuration detected. Please fix JSON syntax errors in the Configuration tab.
          </Typography>
          {typeof currentConfig === 'string' && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              The configuration appears to be invalid JSON. Please check for syntax errors.
            </Typography>
          )}
        </Alert>
      </div>
    );
  }
  
  // Track if config was auto-enhanced with layout values
  const [configAutoEnhanced, setConfigAutoEnhanced] = React.useState(false);
  const [enhancementSnackbarOpen, setEnhancementSnackbarOpen] = React.useState(false);

  // Handle config updates from TabbedAnalyticsDashboard
  const handleConfigUpdate = React.useCallback((updatedConfig: any) => {
    // Update the form state with the enhanced config
    configInput.field.onChange(updatedConfig);
    setConfigAutoEnhanced(true);
    setEnhancementSnackbarOpen(true);
  }, [configInput.field]);

  return (
    <div style={{ backgroundColor: '#ffffff', padding: '16px', borderRadius: '8px', minHeight: '400px' }}>
      {configInput.fieldState.isDirty && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: '#e3f2fd', 
          borderRadius: 1,
          border: '1px solid #2196f3',
          color: '#1565c0'
        }}>
          <Typography variant="body2">
            🔄 Configuration changed - preview will update with new mock data generation
          </Typography>
        </Box>
      )}
      {configAutoEnhanced && (
        <Box sx={{ 
          mb: 2, 
          p: 2, 
          backgroundColor: '#fff3cd', 
          borderRadius: 1,
          border: '1px solid #ffc107',
          color: '#856404'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            ✨ Layout configuration auto-enhanced
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            Missing layout.order and layout.span values have been automatically added to your visualizations. 
            Please save your configuration to persist these changes.
          </Typography>
        </Box>
      )}
      <DashboardAiProviderWrapper dashboardKey={record?.key}>
        <TabbedAnalyticsDashboard
          key={configKey} // Force re-mount when config changes to regenerate mock data
          config={previewConfig}
          onTabChange={() => {}}
          dashboardKey={record?.key} // Pass the dashboard.key for application routing
          onConfigUpdate={handleConfigUpdate}
        />
      </DashboardAiProviderWrapper>
      <Snackbar
        open={enhancementSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setEnhancementSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setEnhancementSnackbarOpen(false)} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          Configuration has been auto-enhanced with layout values. Please save to persist changes.
        </Alert>
      </Snackbar>
    </div>
  );
};

const ConfigurationTab: React.FC = () => {
  const record: any = useRecordContext();
  const configInput = useInput({ source: 'currentConfig.config' });
  const [infoDialogOpen, setInfoDialogOpen] = React.useState(false);

  if (!record?.currentConfig?.config && !configInput.field.value) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <p>No configuration available for editing</p>
        <p>The configuration editor will appear here once a template is selected and saved.</p>
      </div>
    );
  }

  const currentValue = configInput.field.value || record.currentConfig.config;
  
  // Check if mock data related configuration has changed
  const mockDataConfigChanged = React.useMemo(() => {
    if (!configInput.fieldState.isDirty) return false;
    
    const originalConfig = record?.currentConfig?.config;
    const currentConfig = configInput.field.value;
    
    if (!originalConfig || !currentConfig) return false;
    
    // Check if mockDataConfig, dataSource, or dataSets have changed
    const mockDataChanged = JSON.stringify(originalConfig.mockDataConfig) !== JSON.stringify(currentConfig.mockDataConfig);
    const dataSourceChanged = originalConfig.dataSource !== currentConfig.dataSource;
    const dataSetsChanged = JSON.stringify(originalConfig.dataSets) !== JSON.stringify(currentConfig.dataSets);
    
    return mockDataChanged || dataSourceChanged || dataSetsChanged;
  }, [configInput.fieldState.isDirty, configInput.field.value, record?.currentConfig?.config]);

  return (
    <div style={{ padding: '20px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="h5" component="h3">
          Dashboard Configuration
        </Typography>
        <IconButton
          size="small"
          onClick={() => setInfoDialogOpen(true)}
          sx={{ color: 'primary.main' }}
          title="Layout configuration help"
        >
          <InfoIcon />
        </IconButton>
      </Box>
      <p style={{ marginBottom: '20px', color: '#666' }}>
        Edit the dashboard configuration JSON below. Changes will be tracked in the form state - use the Save button to persist changes.
      </p>
      <JsonEditorAce
        value={currentValue}
        onChange={(value) => {
          // This now integrates with react-admin's form state
          configInput.field.onChange(value);
        }}
      />
      {configInput.fieldState.isDirty && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          backgroundColor: '#fff3cd', 
          borderRadius: 1,
          border: '1px solid #ffeaa7',
          color: '#856404'
        }}>
          <Typography variant="body2">
            ⚠️ Configuration has been modified. Save the form to persist changes.
            {mockDataConfigChanged && (
              <>
                <br />
                🔄 Mock data configuration changed - preview will regenerate data automatically.
              </>
            )}
          </Typography>
        </Box>
      )}
      
      {/* Layout Configuration Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon color="primary" />
          Layout Configuration Guide
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: '70vh', overflow: 'auto', pr: 1 }}>
            <Typography variant="h5" sx={{ mt: 1, mb: 2, fontWeight: 600 }}>
              Overview
            </Typography>
            <Typography variant="body1" paragraph>
              Dashboard visualizations are arranged using a <strong>12-column grid system</strong> (Material-UI standard). 
              Each visualization can specify its position and width using <code>layout.order</code> and <code>layout.span</code> properties.
            </Typography>

            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Layout Properties
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>
              <code>layout.order</code>
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, pl: 2 }}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>Type:</strong> Number (1-based)</li>
                <li><strong>Purpose:</strong> Determines the rendering sequence (left to right, top to bottom)</li>
                <li><strong>Example:</strong> <code>"order": 1</code> renders first, <code>"order": 2</code> renders second, etc.</li>
                <li><strong>Note:</strong> Lower numbers render first. Items are sorted by order before rendering.</li>
              </ul>
            </Typography>

            <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, fontWeight: 600 }}>
              <code>layout.span</code>
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 2, pl: 2 }}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>Type:</strong> Number (1-12)</li>
                <li><strong>Purpose:</strong> Number of columns out of 12 the visualization occupies</li>
                <li><strong>Default:</strong> <code>12</code> (full width) if not specified</li>
                <li><strong>Examples:</strong>
                  <ul style={{ marginTop: '4px', paddingLeft: '20px' }}>
                    <li><code>span: 3</code> = 25% width (4 cards per row)</li>
                    <li><code>span: 4</code> = 33% width (3 cards per row)</li>
                    <li><code>span: 6</code> = 50% width (2 cards per row)</li>
                    <li><code>span: 8</code> = 67% width (chart with space)</li>
                    <li><code>span: 12</code> = 100% width (full row)</li>
                  </ul>
                </li>
              </ul>
            </Typography>

            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Row Breaks
            </Typography>
            <Typography variant="body2" paragraph>
              Rows break <strong>automatically</strong> when the cumulative span exceeds 12 columns. 
              The grid system wraps items to a new row when needed.
            </Typography>

            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Example Layout
            </Typography>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#1e1e1e',
                color: '#d4d4d4',
                padding: 2,
                borderRadius: 1,
                overflow: 'auto',
                fontSize: '0.875rem',
                mb: 2,
                fontFamily: 'monospace',
                lineHeight: 1.5,
                border: '1px solid #3e3e3e'
              }}
            >
{`{
  "visualizations": [
    {
      "id": "kpi-1",
      "layout": { "span": 3, "order": 1 }
    },
    {
      "id": "kpi-2",
      "layout": { "span": 3, "order": 2 }
    },
    {
      "id": "kpi-3",
      "layout": { "span": 3, "order": 3 }
    },
    {
      "id": "kpi-4",
      "layout": { "span": 3, "order": 4 }
    },
    {
      "id": "chart",
      "layout": { "span": 8, "order": 5 }
    }
  ]
}`}
            </Box>
            <Typography variant="body2" paragraph>
              <strong>Result:</strong>
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2, mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>Row 1:</strong> 4 KPIs (3+3+3+3 = 12 columns)</li>
                <li><strong>Row 2:</strong> Chart (8 columns, 4 empty columns on right)</li>
              </ul>
            </Typography>

            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Tips
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2, mb: 2 }}>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>Keep spans summing to ≤12</strong> for items you want on the same row</li>
                <li><strong>Use <code>span: 12</code></strong> to force a full-width item (always starts a new row)</li>
                <li><strong>Sequential order values</strong> (1, 2, 3...) are recommended for clarity</li>
                <li><strong>Missing layout values</strong> are auto-enhanced with inferred values based on visualization type</li>
              </ol>
            </Typography>

            <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
              Auto-Enhancement
            </Typography>
            <Typography variant="body2" paragraph>
              If visualizations don't have explicit <code>layout.order</code> or <code>layout.span</code> values, 
              the system will automatically infer them:
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2, mb: 2 }}>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                <li><strong>Cards</strong> (KPI, Performance, AI Insight): <code>span: 3</code>, ordered first</li>
                <li><strong>Charts:</strong> <code>span: 12</code>, ordered after cards</li>
                <li><strong>Tables:</strong> <code>span: 12</code>, ordered after charts</li>
              </ul>
            </Typography>
            <Typography variant="body2" paragraph>
              You'll be notified when auto-enhancement occurs, and you can save the enhanced configuration.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setInfoDialogOpen(false)}>Close</MuiButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// Component that sets focus when record loads (must be inside Edit context)
// FocusSetter component removed - focusKey is now managed by useAgenticContext hook in DashboardFormWrapper

const ValidationTab: React.FC = () => {
  const record: any = useRecordContext();
  
  // Get the current form values to validate the latest changes
  const configInput = useInput({ source: 'currentConfig.config' });
  const config = configInput.field.value || record?.currentConfig?.config;
  
  const {
    validationResult,
    isValidating,
    validate,
    formatResults
  } = useConfigValidation(config, { validateOnChange: true });

  const handleCopyResults = () => {
    if (validationResult) {
      navigator.clipboard.writeText(formatResults());
    }
  };

  if (!config) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        No configuration available for validation. The validation results will appear here once a template is selected and saved.
      </div>
    );
  }

  return (
    <ConfigValidationPanel
      validationResult={validationResult}
      isValidating={isValidating}
      onValidate={validate}
      onCopyResults={handleCopyResults}
    />
  );
};

// Hook to fetch derived application
const useDerivedApplication = (dashboardRecord: any) => {
  const [derivedApplication, setDerivedApplication] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const dataProvider = useDataProvider();

  const refetch = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const fetchDerivedApplication = async () => {
      if (!dashboardRecord?.key && !dashboardRecord['analytics-dashboardsId']) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const entityKey = dashboardRecord.key;

        // Fetch application derived from this dashboard
        const { data } = await dataProvider.getList('application', {
          filter: {
            'derivedBy.entityKey': entityKey,
            _includeDerived: true  // Special flag to include derived entities in results
          },
          pagination: { page: 1, perPage: 10 },
          sort: { field: 'createdAt', order: 'DESC' }
        });

        if (data && data.length > 0) {
          setDerivedApplication(data[0]);
        } else {
          setDerivedApplication(null);
        }
      } catch (err: any) {
        console.error('Error fetching derived application:', err);
        setError(err.message || 'Failed to fetch derived application');
      } finally {
        setLoading(false);
      }
    };

    fetchDerivedApplication();
  }, [dashboardRecord?.id, dashboardRecord?.key, dashboardRecord?.['analytics-dashboardsId'], dataProvider, refreshTrigger]);

  return { derivedApplication, loading, error, refetch };
};

// Hook to fetch agent derived from the derived application
const useDerivedAgent = (derivedApplication: any) => {
  const [derivedAgent, setDerivedAgent] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const dataProvider = useDataProvider();

  const refetch = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  useEffect(() => {
    const fetchDerivedAgent = async () => {
      if (!derivedApplication?.key) {
        setDerivedAgent(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const entityKey = derivedApplication.key;

        // Fetch AI assistant derived from the application
        const { data } = await dataProvider.getList('aiassistant', {
          filter: {
            'derivedBy.entityKey': entityKey,
            _includeDerived: true  // Special flag to include derived entities in results
          },
          pagination: { page: 1, perPage: 10 },
          sort: { field: 'createdAt', order: 'DESC' }
        });

        if (data && data.length > 0) {
          console.log('✅ [useDerivedAgent] Found derived agent from application:', data[0]);
          setDerivedAgent(data[0]);
        } else {
          console.log('ℹ️ [useDerivedAgent] No derived agent found for application:', entityKey);
          setDerivedAgent(null);
        }
      } catch (err: any) {
        console.error('Error fetching derived agent:', err);
        setError(err.message || 'Failed to fetch derived agent');
      } finally {
        setLoading(false);
      }
    };

    fetchDerivedAgent();
  }, [derivedApplication?.id, derivedApplication?.key, dataProvider, refreshTrigger]);

  return { derivedAgent, loading, error, refetch };
};

// Functions Tab Component
const DerivedApplicationFunctionsTab: React.FC = () => {
  const record: any = useRecordContext();
  const derivedAppContext = React.useContext(DerivedApplicationContext);
  const { derivedApplication, loading, error, refetch } = derivedAppContext || { derivedApplication: null, loading: false, error: null, refetch: () => {} };
  const [modifiedFunctions, setModifiedFunctions] = useState<any[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [forceRegeneration, setForceRegeneration] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Initialize modified functions when derived application loads
  useEffect(() => {
    if (derivedApplication?.configuration?.functions) {
      setModifiedFunctions([...derivedApplication.configuration.functions]);
      setHasChanges(false);
    }
  }, [derivedApplication]);

  const handleFunctionChange = (index: number, updatedFunction: any) => {
    const newFunctions = [...modifiedFunctions];
    newFunctions[index] = updatedFunction;
    setModifiedFunctions(newFunctions);
    setHasChanges(true);
  };

  const handleSaveFunctions = async () => {
    if (!derivedApplication || !hasChanges) return;

    setSaving(true);
    try {
      // Update the derived application with modified functions
      await dataProvider.update('application', {
        id: derivedApplication.id,
        data: {
          ...derivedApplication,
          configuration: {
            ...derivedApplication.configuration,
            functions: modifiedFunctions,
            requiresRegeneration: forceRegeneration  // Use checkbox value
          }
        },
        previousData: derivedApplication
      });

      // Also update the dashboard's pendingFunctionImplementations
      if (record?.id) {
        try {
          // Fetch current dashboard to get existing pendingFunctionImplementations
          const currentDashboard = await (dataProvider as any).getOne('analytics-dashboards', record.id);
          
          // Map modified functions to pendingFunctionImplementations format
          const pendingImplementations = modifiedFunctions
            .filter((fn: any) => fn.implementationConfig?.payload?.sql || fn.implementationConfig) // Only include functions with implementations
            .map((fn: any) => ({
              operationKey: fn.operationKey,
              name: fn.name,
              description: fn.description,
              method: fn.method,
              path: fn.path,
              executionProviderKey: fn.executionProviderKey,
              implementationConfig: fn.implementationConfig,
              requiresAuthentication: fn.requiresAuthentication,
              // Backward compatibility
              mock: fn.mock || '',
              webhookUrl: fn.webhookUrl || ''
            }));

          // Update dashboard with pendingFunctionImplementations
          if (pendingImplementations.length > 0) {
            await dataProvider.update('analytics-dashboards', {
              id: record.id,
              data: {
                ...currentDashboard,
                pendingFunctionImplementations: pendingImplementations
              },
              previousData: currentDashboard
            });
            console.log(`✅ Updated dashboard ${record.id} with ${pendingImplementations.length} pending function implementations`);
          }
        } catch (dashboardErr: any) {
          // Non-critical error - log but don't fail the save
          console.warn('⚠️ Could not update dashboard pendingFunctionImplementations:', dashboardErr);
        }
      }

      const message = forceRegeneration 
        ? '✅ Function implementations saved - application will regenerate'
        : '✅ Function implementations saved successfully';
      
      notify(message, { type: 'success' });
      setHasChanges(false);
      
      // Refetch the derived application to show updated data
      refetch();
    } catch (err: any) {
      console.error('❌ Error saving functions:', err);
      notify(`Failed to save functions: ${err.message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading derived application...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading derived application: {error}
        </Alert>
      </Box>
    );
  }

  if (!derivedApplication) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No derived application found yet. The dashboard's application will be automatically generated on the backend.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2, p: 2, bgcolor: '#000000', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #333' }}>
        <Box>
          <Typography variant="body2" sx={{ color: '#4fc3f7' }}>
            <strong>Derived Application:</strong> {derivedApplication.name || derivedApplication.key}
          </Typography>
          <Typography variant="caption" sx={{ color: '#b0bec5' }}>
            Configure implementations for auto-generated API endpoints to power your dashboard's backend.
          </Typography>
        </Box>
        {hasChanges && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={forceRegeneration}
                  onChange={(e) => setForceRegeneration(e.target.checked)}
                  sx={{ 
                    color: '#b0bec5',
                    '&.Mui-checked': { color: '#ff9800' }
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color: '#b0bec5' }}>
                  Force Regeneration
                </Typography>
              }
            />
            {saving && <CircularProgress size={20} />}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveFunctions}
              disabled={saving}
              label={saving ? 'Saving...' : 'Save Functions'}
            />
          </Box>
        )}
      </Box>
      
      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes to function implementations. Click "Save Functions" to persist your changes.
          {forceRegeneration && (
            <><br /><strong>⚠️ Force Regeneration is enabled - the application code will be regenerated on save.</strong></>
          )}
        </Alert>
      )}
      
      {modifiedFunctions && modifiedFunctions.length > 0 ? (
        <Box>
          <Typography variant="h6" gutterBottom>
            API Function Implementations
          </Typography>
          <Grid container spacing={2}>
            {modifiedFunctions.map((func: any, index: number) => (
              <Grid item xs={12} key={func.id || index}>
                <Card variant="outlined">
                  <CardContent>
                    <AIFunctionEdit
                      item={func}
                      onChange={(updatedFunc) => handleFunctionChange(index, updatedFunc)}
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Alert severity="info">
          No functions defined yet. Functions will be automatically generated from the dashboard's data endpoints.
        </Alert>
      )}
    </Box>
  );
};

// Agent Configuration Tab Component
const DerivedAgentConfigurationTab: React.FC = () => {
  const record: any = useRecordContext();
  const derivedAppContext = React.useContext(DerivedApplicationContext);
  const { derivedApplication, loading: appLoading, error: appError } = derivedAppContext || { derivedApplication: null, loading: false, error: null };
  
  // Fetch the agent derived from the application
  const { derivedAgent, loading: agentLoading, error: agentError, refetch } = useDerivedAgent(derivedApplication);
  
  const [instructions, setInstructions] = useState<string>('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const dataProvider = useDataProvider();
  const notify = useNotify();

  // Initialize instructions when derived agent loads
  React.useEffect(() => {
    console.log('🔍 [Dashboard Agent Config] Derived agent changed:', derivedAgent);
    console.log('🔍 [Dashboard Agent Config] Instructions path check:', {
      'derivedAgent.instructions': derivedAgent?.instructions,
      'derivedAgent.configuration': derivedAgent?.configuration,
      'derivedAgent.configuration.instructions': derivedAgent?.configuration?.instructions
    });
    
    if (derivedAgent?.configuration?.instructions) {
      console.log('✅ [Dashboard Agent Config] Setting instructions from configuration.instructions');
      setInstructions(derivedAgent.configuration.instructions);
      setHasChanges(false);
    } else if (derivedAgent?.instructions) {
      console.log('✅ [Dashboard Agent Config] Setting instructions from instructions');
      setInstructions(derivedAgent.instructions);
      setHasChanges(false);
    }
  }, [derivedAgent]);

  const handleInstructionsChange = (value: string) => {
    setInstructions(value);
    setHasChanges(value !== (derivedAgent?.configuration?.instructions || derivedAgent?.instructions || ''));
  };

  const handleSaveInstructions = async () => {
    if (!derivedAgent || !hasChanges) return;

    setSaving(true);
    try {
      // Update the derived agent with modified instructions
      await dataProvider.update('aiassistant', {
        id: derivedAgent.id,
        data: {
          ...derivedAgent,
          configuration: {
            ...derivedAgent.configuration,
            instructions: instructions
          }
        },
        previousData: derivedAgent
      });

      notify('✅ Agent instructions saved successfully', { type: 'success' });
      setHasChanges(false);
      
      // Refetch the derived agent to show updated data
      refetch();
    } catch (err: any) {
      console.error('❌ Error saving agent instructions:', err);
      notify(`Failed to save instructions: ${err.message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (appLoading || agentLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>
          {appLoading ? 'Loading derived application...' : 'Loading derived AI agent...'}
        </Typography>
      </Box>
    );
  }

  if (appError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading derived application: {appError}
        </Alert>
      </Box>
    );
  }

  if (!derivedApplication) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No derived application found yet. The application and agent will be automatically generated on the backend.
        </Alert>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Dashboard key: <strong>{record?.key}</strong>
        </Typography>
      </Box>
    );
  }

  if (agentError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Derived application exists but error loading agent: {agentError}
        </Alert>
      </Box>
    );
  }

  if (!derivedAgent) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No derived AI agent found yet. The agent will be automatically generated when needed.
        </Alert>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
          Application key: <strong>{derivedApplication?.key}</strong>
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2, p: 2, bgcolor: '#000000', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', border: '1px solid #333' }}>
        <Box>
          <Typography variant="body2" sx={{ color: '#4fc3f7', mb: 0.5 }}>
            <strong>Derivation Chain:</strong>
          </Typography>
          <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block' }}>
            Dashboard: <strong>{record?.name}</strong>
          </Typography>
          <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block' }}>
            → Application: <strong>{derivedApplication?.name || derivedApplication?.key}</strong>
          </Typography>
          <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block' }}>
            → Agent: <strong>{derivedAgent?.name || derivedAgent?.assistantKey}</strong>
          </Typography>
        </Box>
        {hasChanges && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {saving && <CircularProgress size={20} />}
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveInstructions}
              disabled={saving}
              label={saving ? 'Saving...' : 'Save Instructions'}
            />
          </Box>
        )}
      </Box>
      
      {hasChanges && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You have unsaved changes to the agent instructions. Click "Save Instructions" to persist your changes.
        </Alert>
      )}
      
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Instructions
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Define how the AI agent should behave and respond to user queries about this dashboard. 
            These instructions guide the agent's personality, expertise, and response style.
          </Typography>
          
          <Box sx={{ mt: 2, minHeight: '500px', height: '60vh' }}>
            <TemplateEditorAce
              value={instructions}
              onChange={handleInstructionsChange}
              mode="text"
            />
          </Box>
          
          {derivedAgent.assistantKey && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#1a202c', borderRadius: 1, border: '1px solid #4a5568' }}>
              <Typography variant="caption" sx={{ color: '#a0aec0' }}>
                <strong style={{ color: '#4fc3f7' }}>Agent Key:</strong> {derivedAgent.assistantKey}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

// App Agent Tab Component
const DerivedApplicationAgentTab: React.FC = () => {
  const record: any = useRecordContext();
  const derivedAppContext = React.useContext(DerivedApplicationContext);
  const { derivedApplication, loading, error } = derivedAppContext || { derivedApplication: null, loading: false, error: null };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading derived application...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading derived application: {error}
        </Alert>
      </Box>
    );
  }

  if (!derivedApplication) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No derived application found yet. The AI agent will be available once the dashboard's application is generated on the backend.
        </Alert>
      </Box>
    );
  }

  if (!derivedApplication.key) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Derived application exists but has no application key set.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ mb: 2, p: 2, bgcolor: '#000000', borderRadius: 1, border: '1px solid #333' }}>
        <Typography variant="body2" sx={{ color: '#4fc3f7' }}>
          <strong>AI Agent for Dashboard:</strong> {derivedApplication.name || derivedApplication.key}
        </Typography>
        <Typography variant="caption" sx={{ color: '#b0bec5' }}>
          This AI agent can answer questions about the data and functionality in your dashboard.
        </Typography>
      </Box>
      
      <Box sx={{ width: '100%' }}>
        <ChatInterface applicationKey={derivedApplication.key} isDrawer={false} />
      </Box>
    </Box>
  );
};

export const AnalyticsDashboardEdit = () => {
  // Initialize the analytics dashboard provider (registers event listeners)
  useEffect(() => {
    initializeAnalyticsDashboardProvider();
  }, []);
  
  // Note: Feature context is now managed by route-based context in App.tsx

  const transform = (data: any) => {
    return {
      ...data,
      // Ensure updatedAt is set when configuration changes
      updatedAt: new Date().toISOString()
    };
  };


  return (
    <Edit transform={transform} actions={<EditDashboardActionsWrapper />}>
      <DashboardFormWrapper />
    </Edit>
  );
};

// Wrapper to provide context to form tabs
const DashboardFormWrapper = () => {
  const record = useRecordContext();
  const derivedAppData = useDerivedApplication(record);
  
  // Set focusKey for the specific dashboard being edited
  // Feature context is managed by route-based context in App.tsx
  useAgenticContext({
    focusKey: record?.id ? String(record.id || record['analytics-dashboardsId']) : undefined,
    clearOnUnmount: true,
  });
  
  return (
    <DerivedApplicationContext.Provider value={derivedAppData}>
      <TabbedForm toolbar={<EditDashboardToolbar />}>
      <FormTab label="Details">
        {/* Hidden component that sets focus when record loads */}
        
        <TextInput source="name" validate={required()} />
        <TextInput source="key" disabled />
        <TextInput source="description" multiline />
        <BooleanInput 
          source="published" 
          label="Published"
          helperText="Enable to make this dashboard visible to end users. Disabled dashboards are only visible in the admin section."
          defaultValue={false}
        />
        <TextInput source="templateId" label="Template Used" disabled />
        <FormattedDateField 
          source="updatedAt" 
          label="Last Updated" 
          helperText="Use 'Refresh From Template' button to update dashboard with latest template changes"
        />
      </FormTab>
      <FormTab label="Preview">
        <PreviewTab />
      </FormTab>
      <FormTab label="Configuration">
        <ConfigurationTab />
      </FormTab>
      <FormTab label="Validation">
        <ValidationTab />
      </FormTab>
      <FormTab label="Functions">
        <DerivedApplicationFunctionsTab />
      </FormTab>
      <FormTab label="Agent Configuration">
        <DerivedAgentConfigurationTab />
      </FormTab>
      <FormTab label="Dashboard Agent">
        <DerivedApplicationAgentTab />
      </FormTab>
      <FormTab label="Version History">
        <FormDataConsumer>
          {({ formData }) => {
            const versions = formData?.versions || [];
            
            if (versions.length === 0) {
              return (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      No Version History
                    </Typography>
                    <Typography color="textSecondary">
                      Version snapshots will be created automatically when you make significant changes to this dashboard.
                    </Typography>
                  </CardContent>
                </Card>
              );
            }
            
            return (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Version History ({versions.length} versions)
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                  Complete history of all configuration changes and published versions
                </Typography>
                
                {versions.slice().reverse().map((version: any, index: number) => (
                  <Card key={version.id || index} sx={{ mb: 2 }}>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography variant="h6">
                              Version {version.versionNumber}
                              {version.versionLabel && ` (${version.versionLabel})`}
                            </Typography>
                            {version.isPublished && (
                              <Chip label="Published" color="success" size="small" />
                            )}
                            {index === 0 && (
                              <Chip label="Current" color="primary" size="small" />
                            )}
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            {version.description}
                          </Typography>
                          {version.createdBy && (
                            <Typography variant="caption" color="textSecondary">
                              Created by: {version.createdBy}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="textSecondary">
                              Created: {new Date(version.createdAt).toLocaleString()}
                            </Typography>
                            {version.publishedAt && (
                              <Typography variant="body2" color="textSecondary">
                                Published: {new Date(version.publishedAt).toLocaleString()}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </Grid>
                      
                      {version.currentConfig && (
                        <Accordion sx={{ mt: 2 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2">View Configuration Snapshot</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ 
                              bgcolor: '#1e1e1e', 
                              color: '#d4d4d4', 
                              p: 2, 
                              borderRadius: 1,
                              overflow: 'auto',
                              maxHeight: '400px'
                            }}>
                              <pre style={{ margin: 0, fontSize: '12px' }}>
                                {JSON.stringify(version.currentConfig, null, 2)}
                              </pre>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}
                      
                      {version.template && (
                        <Accordion sx={{ mt: 2 }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="body2">View Template Snapshot</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ 
                              bgcolor: '#1e1e1e', 
                              color: '#d4d4d4', 
                              p: 2, 
                              borderRadius: 1,
                              overflow: 'auto',
                              maxHeight: '400px'
                            }}>
                              <pre style={{ margin: 0, fontSize: '12px' }}>
                                {JSON.stringify(version.template, null, 2)}
                              </pre>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>
            );
          }}
        </FormDataConsumer>
      </FormTab>
    </TabbedForm>
    </DerivedApplicationContext.Provider>
  );
};